import Image from "next/image";

interface IconProps {
  name:
    | "arrow"
    | "info"
    | "weather"
    | "welcome"
    | "chevron-down"
    | "calendar"
    | "alert"
    | "direct-arrow"
    | "arrow-up-right"
    | "ticket";
  className?: string;
  alt?: string;
  decorative?: boolean;
}

/** Inline SVG icons rendered directly in the DOM. */
const svgIcons: Record<string, React.FC<{ className?: string }>> = {
  "chevron-down": ({ className }) => (
    <svg
      className={className}
      fill="none"
      stroke="currentColor"
      strokeWidth={1.5}
      viewBox="0 0 24 24"
      aria-hidden="true"
      focusable="false"
    >
      <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
    </svg>
  ),
  ticket: ({ className }) => (
    <svg
      className={className}
      viewBox="0 0 40 40"
      fill="none"
      stroke="currentColor"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
      focusable="false"
    >
      <rect x="4" y="8" width="32" height="24" rx="2" strokeWidth="2" />
      <path d="M14 8V32" strokeWidth="2" strokeDasharray="3 3" />
      <circle cx="26" cy="18" r="3" strokeWidth="1.5" />
      <path d="M23 24h6" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  ),
  info: ({ className }) => (
    <svg
      className={className}
      fill="none"
      stroke="currentColor"
      strokeWidth={1.5}
      viewBox="0 0 24 24"
      aria-hidden="true"
      focusable="false"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M11.25 11.25l.041-.02a.75.75 0 011.063.852l-.708 2.836a.75.75 0 001.063.853l.041-.021M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9-3.75h.008v.008H12V8.25z"
      />
    </svg>
  ),
};

// TODO: If the icon set continues to grow, consider migrating to Lucide Icons for scalability.
/** Image-based icons loaded from `/public/icons/`. */
const imgIcons: Record<string, { src: string; defaultAlt: string }> = {
  arrow: { src: "/icons/arrow.svg", defaultAlt: "Arrow icon" },
  weather: { src: "/icons/weather.svg", defaultAlt: "Weather icon" },
  welcome: {
    src: "/icons/welcome.svg",
    defaultAlt: "Grounds for Sculpture logo",
  },
  calendar: { src: "/icons/calendar.svg", defaultAlt: "Calendar icon" },
  alert: { src: "/icons/alert.svg", defaultAlt: "Alert icon" },
  "direct-arrow": { src: "/icons/directArrow.svg", defaultAlt: "Go to link" },
};

/**
 * @component Icon
 * @description Unified icon component that renders either an inline SVG or a Next.js Image.
 * Decorative icons are hidden from screen readers by default.
 * @param name - Name of the icon to reference from the registered SVG or image maps.
 * @param className - CSS classes applied to the rendered element for sizing and color.
 * @param alt - Alternative text for accessibility purposes. Falls back to the icon's default alt.
 * @param decorative - When the icon doesn't have an accessibility purpose. Default is `true` (no purpose).
 * @returns A React element rendering the requested icon, or `null` if the name is unrecognized.
 */
export default function Icon({
  name,
  className,
  alt,
  decorative = true,
}: IconProps) {
  const SvgComponent = svgIcons[name];
  if (SvgComponent) {
    if (decorative) {
      return (
        <span aria-hidden="true">
          <SvgComponent className={className} />
        </span>
      );
    }
    return (
      <span role="img" aria-label={alt}>
        <SvgComponent className={className} />
      </span>
    );
  }

  const icon = imgIcons[name];
  if (icon) {
    const altText = alt || icon.defaultAlt;
    return (
      <Image
        src={icon.src}
        alt={altText}
        width={48}
        height={48}
        aria-hidden={decorative ? "true" : undefined}
        className={className}
        unoptimized
      />
    );
  }

  return null;
}
